/** The one place a feature says it exists.
 *
 * A feature declares WHAT it is; the shell decides WHERE it appears. That
 * inversion is the point: before this, placement was a judgement call made
 * inside a component, so "where do I surface this?" had no answer and the same
 * capability could end up in two homes or none.
 *
 * Everything downstream reads this list — the Tools overlay is generated from
 * it, so the overlay cannot be out of date, and a tool that is not registered
 * genuinely does not exist. See docs/architecture/plans/writing-surface-performance.md's
 * sibling board "Where a feature goes".
 */
import type { LucideIcon } from "lucide-react";

/** The five homes. A tool belongs to exactly one.
 *
 * There is no "write" group: the ink tools live on the annotation toolbar,
 * which is already on screen whenever they apply. Listing them here too would
 * be a second home, which is the thing this registry exists to prevent. */
export type ToolGroup = "create" | "find" | "graph" | "data" | "workspace";

export const TOOL_GROUPS: { id: ToolGroup; label: string }[] = [
  { id: "create", label: "Create" },
  { id: "find", label: "Find" },
  { id: "graph", label: "Graph" },
  { id: "data", label: "Data" },
  { id: "workspace", label: "Workspace" },
];

/** What the shell knows about the moment a tool is offered or run. */
export interface ToolContext {
  /** The document surface in focus, when there is one. */
  doc: { id: string; title: string; kind: "reader" | "markdown" } | null;
  /** Whether the focused surface takes ink (classic engine, markup enabled). */
  canInk: boolean;
  /** The live annotation tool id, so the Write group can show what is active. */
  activeInkTool: string;
  /** Handlers the shell owns and a tool cannot reach on its own. */
  actions: {
    openPalette: () => void;
    addWebsite: () => void;
    smartExport: () => void;
    manageTokens: () => void;
    manageBackups: () => void;
    pickUpload: () => void;
    closeOverlay: () => void;
  };
}

export interface ToolDescriptor {
  /** Stable id. Used for keys, tests and (later) usage telemetry. */
  id: string;
  label: string;
  group: ToolGroup;
  icon: LucideIcon;
  /** Single-key hint shown on the tile. Not bound here; the shell binds it. */
  shortcut?: string;
  /** One line for the tile's title attribute, when the label is not enough. */
  hint?: string;
  /**
   * Relevance, not placement. The only conditional a feature author writes.
   * Omitted means "always offered".
   */
  when?: (ctx: ToolContext) => boolean;
  /** True while this tool is the active mode, for the Write group. */
  isActive?: (ctx: ToolContext) => boolean;
  /** Leaving the overlay open is the exception (picking an ink tool). */
  keepOpen?: boolean;
  run: (ctx: ToolContext) => void;
}

const REGISTRY: ToolDescriptor[] = [];

/**
 * Register one tool. Called at module load from `features/tools.ts`.
 *
 * A repeat id REPLACES rather than throws, because hot module replacement
 * re-executes the definitions file on every edit and a throw there kills the
 * update and leaves the overlay stale. A genuine duplicate in source is caught
 * by `tool-registry.test.ts` instead, which is where this project keeps its
 * rules anyway.
 */
export function defineTool(tool: ToolDescriptor): ToolDescriptor {
  const at = REGISTRY.findIndex((t) => t.id === tool.id);
  if (at >= 0) REGISTRY[at] = tool;
  else REGISTRY.push(tool);
  return tool;
}

/** Every registered tool, in registration order. */
export function allTools(): readonly ToolDescriptor[] {
  return REGISTRY;
}

/** The tools of one group that are relevant right now. */
export function toolsInGroup(group: ToolGroup, ctx: ToolContext): ToolDescriptor[] {
  return REGISTRY.filter((t) => t.group === group && (t.when?.(ctx) ?? true));
}

/** Free-text match over label and hint, for the overlay's search field. */
export function searchTools(q: string, ctx: ToolContext): ToolDescriptor[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return [];
  return REGISTRY.filter((t) => {
    if (!(t.when?.(ctx) ?? true)) return false;
    return `${t.label} ${t.hint ?? ""}`.toLowerCase().includes(needle);
  });
}
